import { faFileLines, faFilePdf } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useState, useEffect, useCallback } from "react";
import {
  Stack as BootstrapStack,
  Accordion,
  Button,
  Container,
  Row,
  Form,
} from "react-bootstrap";
import { useCookies } from "react-cookie";
import { Link } from "react-router-dom";

import {
  useGetLaiValidationDatesQuery,
  useUpdateLaiValidationDatesMutation,
} from "api/memberPortal/lai/lai.api";
import ReactSelect from "components/ReactSelect";
import {
  downloadFile,
  downloadAllFiles,
  getFilesBySourceAndId,
  getPclFiles,
} from "components/UI/Functions/fileFunctions";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";

import Teams from "../Teams";

import CalendarEventCreator from "./CalendarEventCreator";
import CertificateReviewAccordion from "./CertificateReviewAccordion";
import EventLogAccordion from "./EventLogAccordion";
import GenerateDocAccordion from "./GenerateDoc/GenerateDocAccordion";
import ModificationHistoryAccordion from "./ModificationHistoryAccordion";
import SrValidatorRecAccordion from "./SrValidatorRecAccordion";
import { addToDate } from "utils/timeUtils";

export default function SideInfo({
  product,
  updateSidebarFiles,
  refetch,
  setAlert,
}) {
  const [cookies] = useCookies();
  const [projectPersonnel, setProjectPersonnel] = useState([]);
  const [checkInFiles, setCheckinFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [valCheckoutFiles, setValCheckoutFiles] = useState([]);
  const [evalCheckoutFiles, setEvalCheckoutFiles] = useState([]);
  const [pclFiles, setPclFiles] = useState({});
  const [editDates, setEditDates] = useState(false);
  const [updatedDates, setUpdatedDates] = useState({});
  const [calendar, setCalendar] = useState(null);
  const [earFiles, setEarFiles] = useState([]);
  const [validationDates, setValidationDates] = useState({});
  const [editVendor, setEditVendor] = useState(false);
  const [updatedVendor, setUpdatedVendor] = useState({});
  const [updatedOrg, setUpdatedOrg] = useState();
  const [updatedLaiDates, setUpdatedLaiDates] = useState({});
  const { authToken, csrfToken, currentUser, permissions } = useAuth();
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  // RTK Query hooks for LAI validation dates
  const { data: laiValidationDates } = useGetLaiValidationDatesQuery(
    product?.v_id,
    { skip: !product?.v_id },
  );
  const [updateLaiDates] = useUpdateLaiValidationDatesMutation();

  useEffect(() => {
    setUpdatedOrg(product?.vendor_id);
    const fetchCalendar = async () => {
      const calendarInstance = await CalendarEventCreator({ product, cookies });
      setCalendar(calendarInstance);
    };

    fetchCalendar();
  }, [cookies, product]);

  const fetchEarFiles = useCallback(() => {
    if (product?.product_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}ear/current_product_ears_files/?product_id=${product?.product_id}&get_latest=true`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          setEarFiles(response.data);
        })
        .catch((error) => console.log(error));
    }
  }, [product.product_id]);

  useEffect(() => {
    fetchEarFiles();
  }, [fetchEarFiles]);

  useEffect(() => {
    const fetchData = async () => {
      if (product?.product_id && authToken) {
        try {
          const evalFiles = await getFilesBySourceAndId(
            product.product_id,
            "Evaluator Check Out",
            authToken,
          );
          const valFiles = await getFilesBySourceAndId(
            product.product_id,
            "Validator Check Out",
            authToken,
          );
          const checkInFiles = await getFilesBySourceAndId(
            product.product_id,
            "Check In",
            authToken,
          );
          const otherFiles = await getFilesBySourceAndId(
            product.product_id,
            "Other",
            authToken,
          );
          const pclFilesData = await getPclFiles(product.product_id, authToken);

          setEvalCheckoutFiles(evalFiles);
          setValCheckoutFiles(valFiles);
          setCheckinFiles(checkInFiles);
          setOtherFiles(otherFiles);
          setPclFiles(pclFilesData);
        } catch (error) {
          console.error("Failed to fetch files", error);
          setEvalCheckoutFiles([]);
          setValCheckoutFiles([]);
          setCheckinFiles([]);
          setOtherFiles([]);
          setPclFiles({});
        }
      }
    };
    fetchData();
  }, [authToken, product?.product_id, updateSidebarFiles]);

  useInterval(() => {
    const fetchIntervalData = async () => {
      if (product?.product_id && authToken) {
        try {
          const evalFiles = await getFilesBySourceAndId(
            product.product_id,
            "Evaluator Check Out",
            authToken,
          );
          const valFiles = await getFilesBySourceAndId(
            product.product_id,
            "Validator Check Out",
            authToken,
          );
          const checkInFiles = await getFilesBySourceAndId(
            product.product_id,
            "Check In",
            authToken,
          );
          const otherFiles = await getFilesBySourceAndId(
            product.product_id,
            "Other",
            authToken,
          );
          const pclFilesData = await getPclFiles(product.product_id, authToken);

          setEvalCheckoutFiles(evalFiles);
          setValCheckoutFiles(valFiles);
          setCheckinFiles(checkInFiles);
          setOtherFiles(otherFiles);
          setPclFiles(pclFilesData);
        } catch (error) {
          console.error("Failed to fetch files during interval", error);
          setEvalCheckoutFiles([]);
          setValCheckoutFiles([]);
          setCheckinFiles([]);
          setOtherFiles([]);
          setPclFiles({});
        }
      }
    };

    fetchIntervalData();
  }, 180000); // 3 min

  const loadPersonnelProductId = useCallback(() => {
    if (product?.product_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/personnel/get_all_personnel_by_product_id/?product_id=${product?.product_id}`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          setProjectPersonnel(response.data);
        })
        .catch((error) => {
          setProjectPersonnel([]);
        });
    }
  }, [
    product?.product_id,
    authToken,
    product,
    permissions?.role_type,
    currentUser?.id,
  ]);

  useEffect(() => {
    loadPersonnelProductId();
  }, [loadPersonnelProductId]);

  useInterval(() => {
    loadPersonnelProductId();
  }, 180000); //3 min

  // Update local state when RTK query data changes
  useEffect(() => {
    if (laiValidationDates) {
      setValidationDates(laiValidationDates);
    }
  }, [laiValidationDates]);

  const fileInfoButton = (file) => {
    return (
      <BootstrapStack
        direction="horizontal"
        className="d-flex justify-content-left align-items-start"
        key={file?.file_id}
      >
        {file.file_mime_type === "application/pdf" && (
          <FontAwesomeIcon icon={faFilePdf} color="red" size="xl" />
        )}
        {file.file_mime_type !== "application/pdf" && (
          <FontAwesomeIcon icon={faFileLines} color="gray" size="xl" />
        )}
        <div className="ms-2 text-break">
          <Button
            style={{ overflowWrap: "break-word" }}
            className="text-start m-0 p-0"
            variant="link"
            onClick={() => {
              showLoadingSpinner("Downloading...");
              downloadFile(file, authToken);
              hideLoadingSpinner();
            }}
          >
            {file.file_display_name
              ? file.file_display_name
              : file.file_label
                ? file.file_label
                : file.file_new_name
                  ? file.file_new_name
                  : file.file_name}
          </Button>
          <p className="mb-0 ms-3">
            {file.file_new_name ? file.file_new_name : file.file_name}
          </p>
          <p className="mb-0 ms-3">
            Attached by {file?.uploaded_by?.first_name}{" "}
            {file?.uploaded_by?.last_name} on{" "}
            {file?.uploaded_on &&
              moment.utc(file?.uploaded_on).format("YYYY.MM.DD [at] HHmm")}
          </p>
        </div>
      </BootstrapStack>
    );
  };

  const handleChange = (e) => {
    const value = e.target.value === "" ? null : e.target.value;
    setUpdatedDates({ ...updatedDates, [e.target.name]: value });
  };

  const handleLaiDateChange = (e) => {
    const value = e.target.value === "" ? null : e.target.value;
    setUpdatedLaiDates({ ...updatedLaiDates, [e.target.name]: value });
  };

  const handleClearDate = (fieldName, isLaiField = false) => {
    if (isLaiField) {
      setUpdatedLaiDates({ ...updatedLaiDates, [fieldName]: null });
    } else {
      setUpdatedDates({ ...updatedDates, [fieldName]: null });
    }
  };
  const handleVendorChange = (e) => {
    // Product Only
    const vendorFields = ["vendor_poc", "vendor_email", "vendor_phone"];
    if (vendorFields.includes(e.target.name)) {
      setUpdatedVendor({ ...updatedVendor, [e.target.name]: e.target.value });
    }
    // Vendor_id changes
    else {
      setUpdatedOrg({ ...updatedOrg, [e.target.name]: e.target.value });
    }
  };
  const handleSaveVendor = async () => {
    await axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/${product.product_id}/`,
        { ...updatedVendor },
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then(() => {
        setEditVendor(false);
        refetch();
      })
      .catch((error) => console.log("Unable to update product: " + error));
    await axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}org/organization/${product?.vendor_id?.org_id}/`,
        { ...updatedOrg },
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then(() => {
        setEditVendor(false);
        refetch();
      })
      .catch((error) =>
        console.log("Unable to update Vendor Organization: " + error),
      );
  };

  const handleSaveDates = async () => {
    if (updatedDates?.posted_date) {
      await calendar.createCalendarEvent(
        `VID${product?.v_id} Final Package`,
        "Final Package Due",
        new Date(`${updatedDates?.posted_date}T00:00`),
      );
    }
    if (updatedDates?.term_warn_issue_date) {
      const currentDate = new Date(updatedDates?.term_warn_issue_date);
      const sevenDaysBefore = new Date(currentDate);
      sevenDaysBefore.setDate(currentDate.getDate() - 7);
      const formattedSevenDaysBefore = sevenDaysBefore.toISOString();
      await calendar.createCalendarEvent(
        `VID${product?.v_id} Termination Warning Date`,
        "Termination Warning Date",
        new Date(formattedSevenDaysBefore),
      );
      await calendar.createCalendarEvent(
        `VID${product?.v_id} Termination Warning`,
        "Termination Warning",
        new Date(`${updatedDates?.term_warn_issue_date}T00:00`),
      );
    }
    try {
      // Save product dates
      if (Object.keys(updatedDates).length > 0) {
        await axios.put(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/${product.product_id}/`,
          { ...updatedDates },
          {
            withCredentials: true,
            headers: {
              "X-CSRFToken": csrfToken,
              Authorization: `Token ${authToken}`,
            },
          },
        );
      }

      // Save LAI dates if any were updated
      if (Object.keys(updatedLaiDates).length > 0 && product?.v_id) {
        await updateLaiDates({
          vid: product.v_id,
          ...updatedLaiDates,
        });
      }

      setEditDates(false);
      setUpdatedDates({});
      setUpdatedLaiDates({});
      refetch();
    } catch (error) {
      console.log("Unable to update dates: " + error);
    }
  };
  const vendorInformation = () => {
    if (editVendor && permissions.role_type === "NIAP") {
      return (
        <Container>
          <Row>
            <Form.Group controlId="name">
              <Form.Label>Vendor Name:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="name"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_id?.name}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group controlId="website">
              <Form.Label>Vendor Website:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="website"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_id?.website}
              />
            </Form.Group>
          </Row>
          <Row>
            <p className="mb-0 mt-2">POC Information</p>
          </Row>
          <Row>
            <Form.Group controlId="vendor_poc">
              <Form.Label>Vendor POC Name:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="vendor_poc"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_poc}
              />
            </Form.Group>
          </Row>

          <Row>
            <Form.Group controlId="vendor_email">
              <Form.Label>Vendor POC Email:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="vendor_email"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_email}
              />
            </Form.Group>
          </Row>

          <Row>
            <Form.Group controlId="vendor_phone">
              <Form.Label>Vendor POC Primary Phone:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="vendor_phone"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_phone}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group controlId="primary_address_1">
              <Form.Label>Vendor Primary Address 1:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="primary_address_1"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_id?.primary_address_1}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group controlId="primary_address_2">
              <Form.Label>Vendor Primary Address 2:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="primary_address_2"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_id?.primary_address_2}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group controlId="primary_city">
              <Form.Label>Vendor Primary City:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="primary_city"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_id?.primary_city}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group controlId="primary_state">
              <Form.Label>Vendor Primary State:</Form.Label>
              {updatedOrg && (
                <ReactSelect
                  options={states}
                  newObj={updatedOrg}
                  handleChange={handleVendorChange}
                  name="primary_state"
                  defaultValue={states?.find(
                    (state) => state?.value === updatedOrg?.primary_state,
                  )}
                />
              )}
            </Form.Group>
          </Row>
          <Row>
            <Form.Group controlId="primary_zip">
              <Form.Label>Vendor Primary Zip:</Form.Label>
              <Form.Control
                className="mb-2"
                type="text"
                name="primary_zip"
                onChange={handleVendorChange}
                defaultValue={product?.vendor_id?.primary_zip}
              />
            </Form.Group>
          </Row>
        </Container>
      );
    } else {
      return (
        <>
          <p className="mb-0 fw-bold">{product?.vendor_id?.name}</p>
          <a
            href={product?.vendor_id?.website}
            target="_blank"
            rel="noreferrer"
          >
            {product?.vendor_id?.website}
          </a>
          <p className="mb-0 mt-2">POC Information:</p>
          <p className="mb-0 mx-2">{product?.vendor_poc}</p>
          <p className="mb-0 mx-2">{product?.vendor_email}</p>
          <p className="mb-2 mx-2">{product?.vendor_phone}</p>
          <p className="mb-0">Address:</p>
          <p className="mb-1 mx-2">
            {product?.vendor_id?.primary_address_1}
            {product?.vendor_id?.primary_address_2 && (
              <> {product.vendor_id.primary_address_2}</>
            )}
            {product?.vendor_id?.primary_city && (
              <> {product.vendor_id.primary_city}</>
            )}
            {product?.vendor_id?.primary_state && (
              <> {product.vendor_id.primary_state},</>
            )}
            {product?.vendor_id?.primary_zip && (
              <> {product.vendor_id.primary_zip}</>
            )}
          </p>
        </>
      );
    }
  };

  // Date field configuration for better maintainability
  const dateFields = [
    {
      name: "congrats_letter_date",
      label: "Congratulatory Letter Date",
      value: product?.congrats_letter_date,
    },
    {
      name: "term_warn_issue_date",
      label: "Warning Letter Date",
      value: product?.term_warn_issue_date,
    },
    {
      name: "record_closeout_due_date",
      label: "Record Closeout Due Date",
      value: addToDate(product?.posted_date, 1, "weeks"),
    },
    {
      name: "record_closeout_date",
      label: "Record Closeout Date",
      value: product?.record_closeout_date,
    },
    {
      name: "withdrawal_date",
      label: "Withdrawal Date",
      value: product?.withdrawal_date,
    },
    {
      name: "icc_presented_date",
      label: "Certificate Presented Date",
      value: product?.icc_presented_date,
    },
    {
      name: "certificate_printed_date",
      label: "Certificate Printed Date",
      value: product?.certificate_printed_date,
    },
    {
      name: "termination_date",
      label: "Terminated Date",
      value: product?.termination_date,
    },
    {
      name: "due",
      label: "Validation Team Due Date",
      value: validationDates?.due,
      isLaiField: true,
    },
    {
      name: "comp",
      label: "Validation Team Completion Date",
      value: validationDates?.comp,
      isLaiField: true,
    },
  ];

  // Helper function to format date with proper fallback
  const formatDateValue = (dateValue) => {
    if (!dateValue) return "";
    try {
      return moment.utc(dateValue).format("YYYY-MM-DD");
    } catch (error) {
      console.warn("Error formatting date:", dateValue, error);
      return "";
    }
  };

  // Helper function to get the current value for a field (either updated or original)
  const getCurrentFieldValue = (field) => {
    if (field.isLaiField) {
      // For LAI fields, check updatedLaiDates first, then fall back to original value
      return updatedLaiDates[field.name] !== undefined
        ? updatedLaiDates[field.name]
        : field.value;
    } else {
      // For product fields, check updatedDates first, then fall back to original value
      return updatedDates[field.name] !== undefined
        ? updatedDates[field.name]
        : field.value;
    }
  };

  // Helper function to render a single date field
  const renderDateField = (field) => {
    const currentValue = getCurrentFieldValue(field);
    const hasValue = currentValue && currentValue !== "";

    return (
      <TextField
        key={field.name}
        name={field.name}
        label={field.label}
        type="date"
        size="small"
        fullWidth
        variant="outlined"
        value={formatDateValue(currentValue)}
        onChange={field.isLaiField ? handleLaiDateChange : handleChange}
        slotProps={{
          inputLabel: {
            shrink: true,
          },
          input: hasValue
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleClearDate(field.name, field.isLaiField)
                      }
                      edge="end"
                      title="Clear date"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined,
        }}
      />
    );
  };

  const projectDates = () => {
    if (editDates && permissions.role_type === "NIAP") {
      return (
        <Box sx={{ p: 1 }}>
          <Stack spacing={2}>{dateFields.map(renderDateField)}</Stack>
        </Box>
      );
    } else {
      return (
        <Box sx={{ p: 1 }}>
          {/* WARN: Custom styling from pre standardization! */}
          {/* TODO: Standardize when you come across this!  */}
          {dateFields.map((field) => (
            <Typography variant="body2" key={field.name} sx={{ mb: 1 }}>
              <strong>{field.label}:</strong>{" "}
              {field.value
                ? moment.utc(field.value).format("MM/DD/YYYY")
                : "No Date"}
            </Typography>
          ))}
        </Box>
      );
    }
  };
  const [states, setStates] = useState([]);

  const handleFormVendor = () => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}address/states/get_all_states/`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then((response) => {
        const states = response.data?.map((data) => {
          return { value: data.us_state_abbrv, label: data.us_state_name };
        });
        setStates(states);
      })
      .catch((error) => console.log(error));
    setEditVendor(true);
  };

  const handleDownloadAllFiles = async (id, source_type, authToken) => {
    showLoadingSpinner("Downloading...");
    await downloadAllFiles(id, source_type, authToken);
    hideLoadingSpinner();
  };

  return (
    <div>
      {permissions?.role_type === "NIAP" && (
        <>
          <GenerateDocAccordion
            productId={product.product_id}
            setAlert={setAlert}
          />
          <EventLogAccordion product={product} />
        </>
      )}
      <ModificationHistoryAccordion product={product} />
      {permissions?.role_type !== "Vendor" && (
        <CertificateReviewAccordion product_id={product.product_id} />
      )}
      <Teams projectPersonnel={projectPersonnel} />
      <SrValidatorRecAccordion product={product} />
      <Accordion flush className="border border-dark mb-3" defaultActiveKey="1">
        <Accordion.Item eventKey="1">
          <Accordion.Header className="bg-secondary">
            <h6 className="sidebar-title">Project Dates</h6>
          </Accordion.Header>
          <Accordion.Body className="bg-light p-1">
            {!editDates && permissions.role_type === "NIAP" && (
              <div className="d-flex justify-content-end">
                <EditIcon
                  color="action"
                  className="clickable"
                  onClick={() => setEditDates(true)}
                />
              </div>
            )}
            {editDates && (
              <div className="d-flex justify-content-end">
                <SaveIcon
                  color="action"
                  className="clickable"
                  onClick={handleSaveDates}
                />
              </div>
            )}
            {projectDates()}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Accordion flush className="border border-dark mb-3" defaultActiveKey="1">
        <Accordion.Item eventKey="1">
          <Accordion.Header className="bg-secondary">
            <h6 className="sidebar-title">Vendor Information</h6>
          </Accordion.Header>
          <Accordion.Body className="bg-light p-1">
            {!editVendor && permissions.role_type === "NIAP" && (
              <div className="d-flex justify-content-end">
                <EditIcon
                  color="action"
                  className="clickable"
                  onClick={() => handleFormVendor()}
                />
              </div>
            )}
            {editVendor && permissions.role_type === "NIAP" && (
              <div className="d-flex justify-content-end">
                <SaveIcon
                  color="action"
                  className="clickable"
                  onClick={() => handleSaveVendor()}
                />
              </div>
            )}
            {vendorInformation()}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      {product?.assigned_lab && (
        <Accordion
          flush
          className="border border-dark mb-3"
          defaultActiveKey="1"
        >
          <Accordion.Item eventKey="1">
            <Accordion.Header className="bg-secondary">
              <h6 className="sidebar-title">CCTL Information</h6>
            </Accordion.Header>
            <Accordion.Body className="bg-light p-1">
              <p className="m-0 fw-bold">{product?.assigned_lab?.name}</p>
              <a
                href={product?.assigned_lab?.website}
                target="_blank"
                rel="noreferrer"
              >
                {product?.assigned_lab?.website}
              </a>
              <p className="mb-2">{product?.assigned_lab?.primary_phone}</p>
              <p className="mb-0 mt-2">POC Information:</p>
              <p className="mb-0 mx-2">{product?.assigned_lab?.poc}</p>
              <p className="mb-2 mx-2">{product.assigned_lab.poc_email}</p>
              <p className="mb-0">Address:</p>
              <p className="mb-1 mx-2">
                {product?.assigned_lab?.primary_address_1}
                {product?.assigned_lab?.primary_address_2 && (
                  <> {product.assigned_lab.primary_address_2}</>
                )}
                {product?.assigned_lab?.primary_city && (
                  <> {product.assigned_lab.primary_city}</>
                )}
                {product?.assigned_lab?.primary_state && (
                  <> {product.assigned_lab.primary_state},</>
                )}
                {product?.assigned_lab?.primary_zip && (
                  <> {product.assigned_lab.primary_zip}</>
                )}
              </p>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
      <Accordion flush className="border border-dark mb-3" defaultActiveKey="1">
        <Accordion.Item eventKey="1">
          <Accordion.Header className="bg-secondary">
            <h6 className="sidebar-title">Project Files</h6>
          </Accordion.Header>
          <Accordion.Body className="bg-light p-1">
            {["Certified", "Finalizing", "In Assurance Maintenance"]?.includes(
              product?.status,
            ) && (
              <Accordion className="mb-1">
                <Accordion.Item eventKey="1">
                  <Accordion.Header className="bg-secondary">
                    <h6 className="text-white">PCL Files</h6>
                  </Accordion.Header>
                  <Accordion.Body style={{ background: "var(--bs-gray-300)" }}>
                    {pclFiles?.eval_files?.map((file) => fileInfoButton(file))}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}
            <Accordion className="mb-1" defaultActiveKey="1">
              <Accordion.Item eventKey="1">
                <Accordion.Header className="bg-secondary">
                  <h6 className="text-white">All Files</h6>
                </Accordion.Header>
                <Accordion.Body style={{ background: "var(--bs-gray-300)" }}>
                  <Button
                    as={Link}
                    to={`/community/products/details/${product?.product_id}/product/files`}
                    variant="primary"
                  >
                    View All
                  </Button>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Accordion className="mb-1">
              <Accordion.Item eventKey="1">
                <Accordion.Header className="bg-secondary">
                  <h6 className="text-white">Evaluator Checkout Files</h6>
                </Accordion.Header>
                <Accordion.Body style={{ background: "var(--bs-gray-300)" }}>
                  {evalCheckoutFiles?.map((file) => fileInfoButton(file))}
                  <Button
                    variant="primary"
                    onClick={() =>
                      handleDownloadAllFiles(
                        product?.product_id,
                        "Evaluator Check Out",
                        authToken,
                      )
                    }
                  >
                    Download All
                  </Button>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Accordion className="mb-1">
              <Accordion.Item eventKey="1">
                <Accordion.Header className="bg-secondary">
                  <h6 className="text-white">Validator Checkout Files</h6>
                </Accordion.Header>
                <Accordion.Body style={{ background: "var(--bs-gray-300)" }}>
                  {valCheckoutFiles?.map((file) => fileInfoButton(file))}
                  <Button
                    variant="primary"
                    onClick={() =>
                      handleDownloadAllFiles(
                        product?.product_id,
                        "Validator Check Out",
                        authToken,
                      )
                    }
                  >
                    Download All
                  </Button>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Accordion className="mb-1">
              <Accordion.Item eventKey="1">
                <Accordion.Header className="bg-secondary">
                  <h6 className="text-white">Evaluator Check-In Files</h6>
                </Accordion.Header>
                <Accordion.Body style={{ background: "var(--bs-gray-300)" }}>
                  {checkInFiles?.map((file) => fileInfoButton(file))}
                  {earFiles?.map((file) => fileInfoButton(file))}
                  <Button
                    variant="primary"
                    onClick={() =>
                      handleDownloadAllFiles(
                        product?.product_id,
                        "Check In",
                        authToken,
                      )
                    }
                  >
                    Download All
                  </Button>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Accordion className="mb-1">
              <Accordion.Item eventKey="1">
                <Accordion.Header className="bg-secondary">
                  <h6 className="text-white">Other Files</h6>
                </Accordion.Header>
                <Accordion.Body style={{ background: "var(--bs-gray-300)" }}>
                  {otherFiles?.map((file) => fileInfoButton(file))}
                  <Button
                    variant="primary"
                    onClick={() =>
                      handleDownloadAllFiles(
                        product?.product_id,
                        "Other",
                        authToken,
                      )
                    }
                  >
                    Download All
                  </Button>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      {product?.assigned_lab && (
        <Accordion
          flush
          className="border border-dark mb-3"
          defaultActiveKey="1"
        >
          <Accordion.Item eventKey="1">
            <Accordion.Header className="bg-secondary">
              <h6 className="sidebar-title">Sync Session</h6>
            </Accordion.Header>
            <Accordion.Body className="bg-light p-1">
              <p className="mb-0 ms-3">
                Sync Required: {product?.sync_required ? "Yes" : "No"}
              </p>
              <p className="mb-0 ms-3">
                Sync Justification:{" "}
                {product?.sync_justification ? product?.sync_justification : ""}
              </p>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
      <Accordion flush className="border border-dark mb-3" defaultActiveKey="1">
        <Accordion.Item eventKey="1">
          <Accordion.Header className="bg-secondary">
            <h6 className="sidebar-title">ICCC Interest</h6>
          </Accordion.Header>
          <Accordion.Body className="bg-light p-1">
            <p className="mb-0 ms-3">
              Vendor Certificate Presentation:{" "}
              {product?.post_to_iccc ? "Present at ICCC" : "No Interest"}
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}
